import type { Attachment, ChatEntry } from "@ark-bot/protocol";

/**
 * What the user sent that the chat does not show yet. A message is drawn
 * the moment Send is pressed, greyed while it is on its way to the box, and
 * gives way to the host's own bubble for it once that arrives on the event
 * stream. One the box never took says so, and can be sent again.
 */
export interface Outgoing {
  /** The page's own id for it; the host's entry has another. */
  readonly id: string;
  readonly agentId: string;
  readonly text: string;
  readonly attachments: readonly Attachment[];
  readonly replyTo?: string | undefined;
  /** The newest bubble's seq when it was sent: the host's entry for it comes after, never before. */
  readonly afterSeq: number;
  /** `sending` until the box answers the post, `sent` once it has, `failed` if it would not. */
  readonly status: "sending" | "sent" | "failed";
  readonly error?: string | undefined;
}

const sameAttachments = (a: readonly Attachment[], b: readonly Attachment[]): boolean => a.length === b.length && a.every((one, index) => one.blobId === b[index]!.blobId);

/**
 * The outgoing messages still to show, once each has been paired with the
 * host's entry for it: the first of the user's entries after it was sent
 * with the same words and pictures, each entry taken once, in the order
 * they were sent. The box keeps the text as it was posted, so the words
 * match exactly. A message that failed can still find its entry: a post
 * whose answer was lost may have reached the box all the same.
 */
export function unsettled(outbox: readonly Outgoing[], messages: readonly ChatEntry[]): Outgoing[] {
  const taken = new Set<string>();
  return outbox.filter((message) => {
    const entry = messages.find((candidate) => candidate.kind === "user" && candidate.seq > message.afterSeq && !taken.has(candidate.id) && candidate.text === message.text && sameAttachments(candidate.attachments, message.attachments));
    if (entry == null) return true;
    taken.add(entry.id);
    return false;
  });
}

/** An outgoing message drawn as the user's bubble it will become. */
export function asEntry(message: Outgoing, now: number): Extract<ChatEntry, { kind: "user" }> {
  return { kind: "user", id: message.id, seq: Number.MAX_SAFE_INTEGER, createdAt: now, text: message.text, attachments: [...message.attachments], ...(message.replyTo == null ? {} : { replyTo: message.replyTo }) };
}
